import { useCallback, useRef, useEffect } from "react";

interface PriceRangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatLabel?: (value: number) => string;
  step?: number;
}

export default function PriceRangeSlider({
  min,
  max,
  value,
  onChange,
  formatLabel = (v) => `$${v}`,
  step = 1,
}: PriceRangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  const leftPercent = ((value[0] - min) / (max - min)) * 100;
  const rightPercent = ((value[1] - min) / (max - min)) * 100;

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = Math.min(Number(e.target.value), value[1] - step);
      onChange([newMin, value[1]]);
    },
    [value, step, onChange],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = Math.max(Number(e.target.value), value[0] + step);
      onChange([value[0], newMax]);
    },
    [value, step, onChange],
  );

  return (
    <div className="space-y-4">
      <div className="relative h-6 flex items-center">
        <div ref={trackRef} className="absolute inset-x-0 h-1.5 rounded-full bg-background-200">
          <div
            className="absolute h-full rounded-full bg-primary-400"
            style={{
              left: `${leftPercent}%`,
              width: `${rightPercent - leftPercent}%`,
            }}
          ></div>
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value[0]}
          onChange={handleMinChange}
          className="absolute inset-x-0 w-full h-6 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary-400 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary-400 [&::-moz-range-thumb]:cursor-pointer"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value[1]}
          onChange={handleMaxChange}
          className="absolute inset-x-0 w-full h-6 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary-400 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary-400 [&::-moz-range-thumb]:cursor-pointer"
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 px-3 py-2 bg-background-100 border border-background-200 rounded-lg">
          <span className="text-xs text-foreground-500 block mb-0.5">Min</span>
          <span className="text-sm font-semibold text-foreground-900">{formatLabel(value[0])}</span>
        </div>
        <span className="text-foreground-400 text-sm">—</span>
        <div className="flex-1 px-3 py-2 bg-background-100 border border-background-200 rounded-lg">
          <span className="text-xs text-foreground-500 block mb-0.5">Max</span>
          <span className="text-sm font-semibold text-foreground-900">{formatLabel(value[1])}</span>
        </div>
      </div>
    </div>
  );
}